import { AxiosResponse } from 'axios';
import api from '~/services/api';
import {
  RouteReportMetrics,
  RoutingClient,
  RoutingFilters,
  SaveAIRouteSettingsPayload,
  VendorClientRoutePayload
} from './routing-interfaces';
import { UserInterface } from '@e3labs/isa-data-dictionary';

class RoutingService {
  getRoutesList({
    skip,
    limit,
    userExternalCode,
    weekNumber,
    month,
    year,
    isCount
  }: {
    skip?: number;
    limit?: number;
    userExternalCode?: string;
    weekNumber?: number;
    month?: number;
    year?: number;
    isCount?: boolean;
  }): Promise<AxiosResponse<UserInterface[] | null>> {
    return api.get('/vendor-client-visit/vendor-client-route/vendors', {
      params: {
        skip,
        limit,
        userExternalCode,
        weekNumber,
        month,
        year,
        isCount
      }
    });
  }

  getRoutesByUserExternalCode(
    userExternalCode: string,
    month: number,
    year: number,
    type?: string
  ): Promise<AxiosResponse<RoutingClient | null>> {
    return api.get('/vendor-client-visit/vendor-client-route', {
      params: {
        userExternalCode,
        month,
        year,
        type
      }
    });
  }

  getSellers({
    queryString,
    userExternalCodes
  }: {
    queryString?: string;
    userExternalCodes?: string;
  }): Promise<AxiosResponse<UserInterface[] | null>> {
    return api.get('/user/subordinates', {
      params: {
        queryString,
        userExternalCodes
      }
    });
  }

  getClientsBySeller(filters?: RoutingFilters): Promise<AxiosResponse<RoutingClient[]>> {
    const {
      seller,
      skip = 0,
      limit = 50,
      clientName,
      state,
      city,
      neighborhood,
      orderBy = 'name',
      orderDirection = 1,
      clientExternalCodes
    } = filters || {};

    return api.get('/client', {
      params: {
        skip,
        limit,
        userExternalCode: seller?.externalCode,
        queryString: clientName,
        state,
        city,
        neighborhood,
        district: neighborhood,
        fieldsToShow:
          '_id,externalCode,clientName,name,governmentalCode,location,blocked,commercialInfo,lastPurchaseDate,nextVisit,openBills,hadSaleThisMonth,hadVendorSaleThisMonth,potentialSalesCustomers,promoterExternalCode,address,city,state,neighborhood,district',
        orderBy,
        orderDirection,
        userRole: 'vendor',
        clientExternalCodes: clientExternalCodes?.length ? clientExternalCodes.join(',') : undefined
      }
    });
  }

  getClientByExternalCode(externalCode: string): Promise<AxiosResponse<RoutingClient[]>> {
    return api.get('/client', {
      params: {
        clientExternalCodes: externalCode,
        skip: 0,
        limit: 1,
        orderBy: 'externalCode',
        orderDirection: 1,
        fieldsToShow:
          '_id,externalCode,clientName,name,address,city,state,neighborhood,commercialInfo,isRouted,location'
      }
    });
  }

  getClientsByExternalCodes(
    clientExternalCodes: string,
    userExternalCode?: string,
    limit?: number
  ): Promise<AxiosResponse<RoutingClient[]>> {
    return api.get('/client', {
      params: {
        skip: 0,
        limit: limit ?? 50,
        userExternalCode,
        clientExternalCodes,
        fieldsToShow:
          '_id,externalCode,clientName,name,governmentalCode,location,blocked,commercialInfo,lastPurchaseDate,nextVisit,openBills,hadSaleThisMonth,hadVendorSaleThisMonth,potentialSalesCustomers,promoterExternalCode,address,city,state,neighborhood,district',
        orderBy: 'name',
        orderDirection: 1,
        userRole: 'vendor'
      }
    });
  }

  getCityLocales(filters?: RoutingFilters) {
    const { skip, limit, fieldsToShow, city, state, queryString } = filters;
    return api.get('/common/city-locales', {
      params: {
        skip,
        limit,
        fieldsToShow,
        city,
        state,
        queryString
      }
    });
  }

  saveAllocations(payload: VendorClientRoutePayload[]): Promise<AxiosResponse<null>> {
    return api.put('/vendor-client-visit/vendor-client-route/set-routes', payload);
  }

  updateRoute(payload: VendorClientRoutePayload[]): Promise<AxiosResponse<null>> {
    return api.put(`/vendor-client-visit/vendor-client-route/set-routes/`, payload);
  }

  deleteRoute(userExternalCode: string): Promise<AxiosResponse<null>> {
    return api.delete(`/vendor-client-visit/vendor-client-route/by-user`, {
      params: {
        userExternalCode
      }
    });
  }

  getWalletRouteSummary(
    userExternalCode: string
  ): Promise<AxiosResponse<{ walletCount: number; routerizedCount: number }>> {
    return api.get('/vendor-client-visit/wallet-route-summary', {
      params: { userExternalCode }
    });
  }

  postGenerateAIRoutes({ userExternalCode }: { userExternalCode: string }) {
    return api.post(`/vendor-client-visit/generate-ai-route`, {
      userExternalCode
    });
  }

  postSaveAIRouteSettings(payload: SaveAIRouteSettingsPayload): Promise<AxiosResponse<any>> {
    return api.post('/vendor-client-visit/generate-ai-route/save', payload);
  }

  getGenerateAIRoutesList({
    userExternalCodes,
    weekNumber,
    month,
    year,
    limit,
    skip
  }: {
    userExternalCodes?: string;
    weekNumber?: number;
    month?: number;
    year?: number;
    limit?: number;
    skip?: number;
  } = {}): Promise<AxiosResponse<any[]>> {
    return api.get('/vendor-client-visit/generate-ai-route', {
      params: {
        userExternalCodes,
        weekNumber,
        month,
        year,
        limit,
        skip
      }
    });
  }

  getGenerateAIRoutesCount({
    userExternalCodes,
    weekNumber,
    month,
    year
  }: {
    userExternalCodes?: string;
    weekNumber?: number;
    month?: number;
    year?: number;
  } = {}): Promise<AxiosResponse<{ count: number }>> {
    return api.get('/vendor-client-visit/generate-ai-route/count', {
      params: {
        userExternalCodes,
        weekNumber,
        month,
        year
      }
    });
  }

  getGenerateAIRouteById(id: string): Promise<AxiosResponse<any>> {
    return api.get(`/vendor-client-visit/generate-ai-route/${id}`);
  }

  getGenerateAIRouteStatus(id: string): Promise<AxiosResponse<any>> {
    return api.get(`/vendor-client-visit/generate-ai-route-status/${id}`);
  }
}

export default new RoutingService();
